import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { getDurationLabel } from '../../utils/utils';
import { debounce } from '../../utils/debounce';
import { isMobile } from 'react-device-detect';
import Select from 'react-select';

const IFBatchPage = () => {
  const [batchNameFilter, setBatchNameFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filterMessage, setFilterMessage] = useState({ type: '', text: '' });
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [batches, setBatches] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0
  });

  const institute_id = sessionStorage.getItem('studentInstituteId');
  const teacher_id = sessionStorage.getItem('id');
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  // Fetch programs for filter dropdown
  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await fetch(
        `${LMS}/api/institutes/${institute_id}/programs`,
        {
          headers: {
            'Authorization': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      const result = await response.json();
      if (result.status) {
        setPrograms(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchBatches = async (page = 1, filters = {}) => {
    setIsLoading(true);
    try {
      const payload = {
        teacher_id: parseInt(teacher_id)
      };

      if (filters.batchName) payload.batch_name = filters.batchName;
      if (filters.program) payload.program_name = filters.program;
      if (filters.status) payload.status = filters.status.toLowerCase();

      const response = await fetch(
        `${LMS}/api/batch/getlist?page=${page}`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();

      if (result.status && result.data) {
        const batchData = result.data.data || [];
        setBatches(batchData);

        setPagination({
          current_page: result.data.current_page,
          last_page: result.data.last_page,
          per_page: result.data.per_page,
          total: result.data.total,
          from: result.data.from,
          to: result.data.to
        });

        if (batchData.length === 0 && (filters.batchName || filters.program || filters.status)) {
          setFilterMessage({
            type: 'warning',
            text: 'No batches found matching your filter criteria.'
          });
        } else if (batchData.length > 0) {
          setFilterMessage({
            type: 'info',
            text: `Showing ${result.data.from} to ${result.data.to} of ${result.data.total} batches.`
          });
        } else {
          setFilterMessage({ type: '', text: '' });
        }
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      setFilterMessage({
        type: 'danger',
        text: 'Failed to fetch batches. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Create a stable debounced function
  const debouncedFetchBatches = useCallback(
    debounce((page, filters) => {
      fetchBatches(page, filters);
    }, 500),
    []
  );

  // Fetch batches when filters change
  useEffect(() => {
    const filters = {
      batchName: batchNameFilter,
      program: programFilter,
      status: statusFilter
    };

    // Use debounced fetch for batch name filter (text input)
    // Use immediate fetch for dropdown filters
    if (batchNameFilter) {
      debouncedFetchBatches(1, filters);
    } else {
      fetchBatches(1, filters);
    }
  }, [batchNameFilter, programFilter, statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const bootstrap = await import('bootstrap/dist/js/bootstrap.bundle.min.js');
        const triggers = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltips = [...triggers].map(
          (t) => new bootstrap.Tooltip(t, { boundary: 'window' })
        );
        return () => tooltips.forEach((t) => t.dispose());
      } catch { }
    })();
  }, [batches]);

  const handleReset = () => {
    setBatchNameFilter('');
    setProgramFilter('');
    setStatusFilter('');
    setFilterMessage({ type: '', text: '' });
    fetchBatches(1, { batchName: '', program: '', status: '' });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchBatches(page);
    }
  };

  const formatTime = (t) => {
    const [h, m] = t.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour.toString().padStart(2, '0')}:${m} ${ampm}`;
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

  const statusBadge = (s) => {
    const status = s?.toLowerCase();
    switch (status) {
      case 'active': return 'bg-success';
      case 'inactive': return 'bg-warning text-dark';
      default: return 'bg-secondary';
    }
  };

  const uniqueStatuses = ['Active', 'Inactive'];

  const renderPagination = () => {
    if (pagination.last_page <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <nav aria-label="Batch pagination">
        <ul className="pagination justify-content-center mb-0">
          <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
            >
              Previous
            </button>
          </li>

          {startPage > 1 && (
            <>
              <li className="page-item">
                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
              </li>
              {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
            </>
          )}

          {pages.map(page => (
            <li key={page} className={`page-item ${pagination.current_page === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(page)}>
                {page}
              </button>
            </li>
          ))}

          {endPage < pagination.last_page && (
            <>
              {endPage < pagination.last_page - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
              <li className="page-item">
                <button className="page-link" onClick={() => handlePageChange(pagination.last_page)}>
                  {pagination.last_page}
                </button>
              </li>
            </>
          )}

          <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  // react-select options
  const programOptions = programs.map(p => ({ value: p.program_title, label: p.program_title }));
  const statusOptions = uniqueStatuses.map(s => ({ value: s, label: s }));

  return (
    <div>
      <div className="col-12">
        <PageHeader
          title="Batches"
          breadcrumbs={[
            { label: 'Dashboard', path: '/teacher-dashboard' },
            { label: 'Batches', active: true },
          ]}
        />
      </div>

      <div className={isMobile ? 'border-0 mb-4 rounded-2 mt-3' : 'mb-4 rounded-2 p-md-3 border'}>
        <div className="row mb-1 d-none d-md-flex">
          <div className="col-12">
            <label className="form-label text-lg fw-semibold text-dark">Filter Options</label>
          </div>
        </div>

        <div className="row mb-md-2">
          <div className="col-12">
            <div className="d-md-none">
              <div className="d-grid gap-2">
                <button className="btn btn-outline-primary" onClick={() => setShowMobileFilters(true)}>
                  <i className="fas fa-filter me-2" /> Filters
                  {(batchNameFilter || programFilter || statusFilter) && (
                    <span className="badge bg-danger ms-2">
                      {[batchNameFilter, programFilter, statusFilter].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="d-none d-md-block">
              <div className="card-body">
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-dark">Batch Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search batch..."
                      value={batchNameFilter}
                      onChange={(e) => setBatchNameFilter(e.target.value)}
                    />
                    {isLoading && batchNameFilter && (
                      <small className="text-muted">
                        <i className="fas fa-circle-notch fa-spin me-1" />
                        Searching...
                      </small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-dark">Program</label>
                    <Select
                      options={programOptions}
                      value={programOptions.find(opt => opt.value === programFilter) || null}
                      onChange={(selected) => setProgramFilter(selected ? selected.value : '')}
                      placeholder="All Programs"
                      isClearable
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-dark">Status</label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(opt => opt.value === statusFilter) || null}
                      onChange={(selected) => setStatusFilter(selected ? selected.value : '')}
                      placeholder="All Status"
                      isClearable
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div className="col-md-2 d-flex">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleReset}
                      disabled={isLoading}
                      title="Reset Filters"
                    >
                      <i className="fas fa-sync-alt" /> Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showMobileFilters && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-md-none"
            style={{ zIndex: 1040 }}
            onClick={() => setShowMobileFilters(false)}
          />
          <div
            className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-md-none"
            style={{
              width: '320px',
              zIndex: 1050,
              transform: showMobileFilters ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s ease-in-out',
              overflowY: 'auto',
            }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-primary text-white">
              <h5 className="mb-0 text-white">
                <i className="fas fa-filter me-2" /> Filters
              </h5>
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => setShowMobileFilters(false)}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="p-3">
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Batch Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search batch..."
                  value={batchNameFilter}
                  onChange={(e) => setBatchNameFilter(e.target.value)}
                />
                {isLoading && batchNameFilter && (
                  <small className="text-muted">
                    <i className="fas fa-circle-notch fa-spin me-1" />
                    Searching...
                  </small>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Program</label>
                <Select
                  options={programOptions}
                  value={programOptions.find(opt => opt.value === programFilter) || null}
                  onChange={(selected) => setProgramFilter(selected ? selected.value : '')}
                  placeholder="All Programs"
                  isClearable
                  classNamePrefix="react-select"
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Status</label>
                <Select
                  options={statusOptions}
                  value={statusOptions.find(opt => opt.value === statusFilter) || null}
                  onChange={(selected) => setStatusFilter(selected ? selected.value : '')}
                  placeholder="All Status"
                  isClearable
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            <div className="p-3 border-top mt-auto">
              <div className="d-grid gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    handleReset();
                    setShowMobileFilters(false);
                  }}
                  disabled={isLoading}
                >
                  <i className="fas fa-sync-alt me-2" /> Reset All Filters
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => setShowMobileFilters(false)}
                >
                  <i className="fas fa-check me-2" /> Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="row">
        <div className="col-12">
          <div className="card skyblueborder">
            <div className="card-header skyblue-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="fas fa-users me-2" /> Batch List
              </h5>
            </div>
            <div className="card-body p-0">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading batches...</p>
                </div>
              ) : batches.length > 0 ? (
                <>
                  {/* Desktop Table View */}
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="border-0 fw-semibold py-3 px-4">Batch Info</th>
                          <th className="border-0 fw-semibold py-3 px-4">Program</th>
                          <th className="border-0 fw-semibold py-3 px-4 text-center">Students</th>
                          <th className="border-0 fw-semibold py-3 px-4">Duration</th>
                          <th className="border-0 fw-semibold py-3 px-4">Type</th>
                          <th className="border-0 fw-semibold py-3 px-4">Days</th>
                          <th className="border-0 fw-semibold py-3 px-4">Session Time</th>
                          <th className="border-0 fw-semibold py-3 px-4">Status</th>
                          <th className="border-0 fw-semibold py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batches.map((batch) => (
                          <tr key={batch.id}>
                            <td className="py-3 px-4">
                              <div className="fw-semibold text-dark">{batch.batch_name}</div>
                              <small className="text-muted">{batch.teacher_name}</small>
                            </td>
                            <td className="py-3 px-4 text-dark">{batch.program_title}</td>
                            <td className="py-3 px-4 text-center">
                              <span className="fw-semibold">{batch.student_count}</span>
                            </td>
                            <td className="py-3 px-4">
                              <small
                                className="pe-auto"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={`${formatDate(batch.start_date)} - ${formatDate(batch.end_date)}`}
                              >
                                {getDurationLabel(batch.start_date, batch.end_date)}
                              </small>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-capitalize">{batch.batch_type}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="fw-semibold">{batch.days}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="fw-semibold">{formatTime(batch.time)}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`badge ${statusBadge(batch.status)}`}>
                                {batch.status}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => window.location.href = 
                                    `/teacher-generate-schedule?batch=${batch.id}&program=${batch.program_id}`
                                  }
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Generate Schedule"
                                >
                                  <i className="fas fa-calendar-plus"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => window.location.href = `/teacher-view-schedule?batch=${batch.id}&program=${batch.program_id}`}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="View Schedule"
                                >
                                  <i className="fas fa-calendar-alt" />
                                </button>
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => window.location.href = `/teacher-student-assignment?batch=${batch.id}`}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Assignments"
                                >
                                  <i className="fas fa-tasks" />
                                </button>
                                <button
                                  className="btn btn-sm bg-success text-white"
                                  onClick={() => window.location.href = `/batch-report?batch=${batch.id}`}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Batch Report"
                                >
                                  <i className="fas fa-file-alt" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="d-md-none p-2">
                    {batches.map((batch) => (
                      <div key={batch.id} className="card mb-3 shadow-sm border">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="fw-semibold text-dark">{batch.batch_name}</div>
                              <small className="text-muted">{batch.teacher_name}</small>
                            </div>
                            <span className={`badge ${statusBadge(batch.status)}`}>
                              {batch.status}
                            </span>
                          </div>

                          <div className="row mb-2 text-dark">
                            <div className="col-6">
                              <small className="text-muted d-block">Program</small>
                              <span>{batch.program_title}</span>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Students</small>
                              <span className="fw-semibold">{batch.student_count}</span>
                            </div>
                          </div>

                          <div className="row mb-2 text-dark">
                            <div className="col-6">
                              <small className="text-muted d-block">Duration</small>
                              <span
                                className="pe-auto"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={`${formatDate(batch.start_date)} - ${formatDate(batch.end_date)}`}
                              >
                                {getDurationLabel(batch.start_date, batch.end_date)}
                              </span>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Type</small>
                              <span className="text-capitalize">{batch.batch_type}</span>
                            </div>
                          </div>

                          <div className="row mb-3 text-dark">
                            <div className="col-6">
                              <small className="text-muted d-block">Days</small>
                              <span className="fw-semibold">{batch.days}</span>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Session Time</small>
                              <span className="fw-semibold">{formatTime(batch.time)}</span>
                            </div>
                          </div>

                          <div className="d-flex gap-1 flex-wrap">
                            <button
                              className="btn btn-sm btn-soft-primary"
                              onClick={() => window.location.href = 
                                `/teacher-generate-schedule?batch=${batch.id}&program=${batch.program_id}`
                              }
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Generate Schedule"
                            >
                              <i className="fas fa-calendar-plus" /> Generate Schedule
                            </button>
                            <button
                              className="btn btn-sm btn-soft-info"
                              onClick={() => window.location.href = `/teacher-view-schedule?batch=${batch.id}&program=${batch.program_id}`}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="View Schedule"
                            >
                              <i className="fas fa-calendar-alt" /> View Schedule
                            </button>
                            <button
                              className="btn btn-sm btn-soft-warning"
                              onClick={() => window.location.href = `/teacher-student-assignment?batch=${batch.id}`}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Assignments"
                            >
                              <i className="fas fa-tasks" /> Assignments
                            </button>
                            <button
                              className="btn btn-sm bg-success text-white"
                              onClick={() => window.location.href = `/batch-report?batch=${batch.id}`}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Batch Report"
                            >
                              <i className="fas fa-file-alt" /> Batch Report
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {pagination.total > pagination.per_page && (
                    <div className="card-footer bg-white border-top py-3">
                      <div className="row align-items-center">
                        <div className="col-md-6 mb-2 mb-md-0">
                          <span className="text-muted">
                            Showing {pagination.from} to {pagination.to} of {pagination.total} batches
                          </span>
                        </div>
                        <div className="col-md-6">
                          {renderPagination()}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-users fa-3x text-muted mb-3" />
                  <h6 className="text-muted">No Batches Found</h6>
                  <p className="text-muted mb-3">
                    {(batchNameFilter || programFilter || statusFilter)
                      ? 'No batches match your current filter criteria.'
                      : 'No batches have been assigned yet.'}
                  </p>
                  {(batchNameFilter || programFilter || statusFilter) ? (
                    <button className="btn btn-outline-primary" onClick={handleReset}>
                      <i className="fas fa-sync-alt me-1" /> Clear Filters
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IFBatchPage;
